import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Mail, Trash2 } from "lucide-react";
import { adminApi, apiError } from "../adminApi";
import { Panel, buttonClass, inputClass } from "../components/Bits";
import { useAdminAuth } from "../AdminAuthContext";

const TestDataPanel = () => {
  const [survey, setSurvey] = useState(null);
  const [deep, setDeep] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = (withDeep) =>
    adminApi.get("/crm/admin/test-data", { params: { deep: withDeep } })
      .then(({ data }) => setSurvey(data)).catch(() => {});

  useEffect(() => { load(deep); }, [deep]);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/crm/admin/test-data/purge", null,
                                           { params: { deep } });
      const total = Object.values(data.removed).reduce((sum, n) => sum + n, 0);
      toast.success(total ? `${total} test rows cleared` : "Nothing left to clear");
      setConfirming(false);
      load(deep);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const counts = Object.entries(survey?.counts || {});
  const total = counts.reduce((sum, [, n]) => sum + n, 0);

  return (
    <Panel title="Test data" testid="panel-test-data">
      <p className="text-sm leading-relaxed text-cream/55">
        Anything the automated tests write is marked and kept out of your activity feed, pipeline and
        scoreboards. This clears those rows out of the database for good. Accounts loaded from public
        filings are never touched.
      </p>

      {counts.length ? (
        <ul className="mt-5 grid gap-2 sm:grid-cols-2" data-testid="test-data-counts">
          {counts.map(([name, value]) => (
            <li key={name} className="flex items-baseline justify-between gap-3 border-b border-cream/10 pb-1.5"
                data-testid={`test-data-count-${name}`}>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                {name.replace(/_/g, " ")}
              </span>
              <span className="font-mono text-xs text-cream/70">{value.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-5 text-sm text-cream/40" data-testid="test-data-clean">
          Nothing to clear — the desk is showing real work only.
        </p>
      )}

      {survey?.touched_accounts?.length ? (
        <p className="mt-4 text-xs leading-relaxed text-cream/40" data-testid="test-data-touched">
          {survey.touched_accounts.length} real account(s) were written against by tests —{" "}
          {survey.touched_accounts.slice(0, 3).map((a) => a.name).join(", ")}
          {survey.touched_accounts.length > 3 ? "…" : ""}. Their stage is left alone; a test-made PEO
          confirmation is reset.
        </p>
      ) : null}

      <label className="mt-5 flex items-center gap-2 text-xs text-cream/50"
             data-testid="test-data-deep-label">
        <input type="checkbox" checked={deep} onChange={(e) => setDeep(e.target.checked)}
               data-testid="test-data-deep-toggle" className="accent-gold" />
        Also clear everything the desk wrote before test tagging existed — activity log, cadence
        enrolments, scheduled touches and deals (your filing accounts and contacts stay)
      </label>

      {confirming ? (
        <div className="mt-4 space-y-3 rounded-lg border border-terra/40 bg-terra/5 px-4 py-3"
             data-testid="test-data-confirm">
          <p className="text-xs leading-relaxed text-cream/70">
            This deletes {total.toLocaleString()} row(s){deep ? " plus every cadence enrolment, touch and deal" : ""}.
            It cannot be undone.
          </p>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={purge} disabled={busy} className={buttonClass}
                    data-testid="test-data-purge-confirm">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Yes, clear it
            </button>
            <button type="button" onClick={() => setConfirming(false)}
                    data-testid="test-data-purge-cancel"
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35 transition-colors duration-200 hover:text-cream">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setConfirming(true)} disabled={!total && !deep}
                data-testid="test-data-purge-button"
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-terra/40 px-4 py-2 text-xs text-terra transition-colors duration-200 hover:bg-terra/10 disabled:opacity-40">
          <Trash2 className="h-3.5 w-3.5" /> Clear test data
        </button>
      )}
    </Panel>
  );
};


const ContactFinderPanel = () => {
  const [status, setStatus] = useState(null);
  const [lookups, setLookups] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    adminApi.get("/crm/enrich/status").then(({ data }) => setStatus(data)).catch(() => {});
    adminApi.get("/crm/enrich/lookups").then(({ data }) => setLookups(data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  if (!status) return null;

  const run = async () => {
    setBusy(true);
    try {
      await adminApi.post("/crm/enrich/filing-contacts", { year: 2024, limit: 5000 });
      toast.success("Reading the filing signatures — this runs in the background");
      setTimeout(load, 6000);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Contact finder" testid="panel-contact-finder">
      <p className="text-sm leading-relaxed text-cream/55" data-testid="contact-finder-note">
        {status.note}
      </p>
      <div className="mt-5 grid gap-5 sm:grid-cols-3">
        {[["Accounts with a named signer", status.accounts_with_filing_contacts],
          ["Accounts with a contact", status.accounts_with_a_contact],
          ["Contacts with an email", status.contacts_with_an_email]].map(([label, value]) => (
          <div key={label}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">{label}</p>
            <p className="mt-1.5 font-serif text-2xl leading-none text-cream">
              {value.toLocaleString()}
            </p>
          </div>
        ))}
      </div>
      <button type="button" onClick={run} disabled={busy} className={`${buttonClass} mt-5`}
              data-testid="contact-finder-run">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Find contacts from filings
      </button>
      {lookups ? (
        <p className="mt-4 text-xs leading-relaxed text-cream/45" data-testid="contact-lookup-status">
          {lookups.connected
            ? `Email lookup is connected. ${lookups.lookups_spent} lookups spent, ` +
              `${lookups.emails_found} emails found. A lookup is only spent when you press ` +
              `"Find the email" on an account or a queued touch.`
            : "Email lookup is not connected yet — add the Apollo API key and the \u201cFind the " +
              "email\u201d buttons start working. Names from the filings work without it."}
        </p>
      ) : null}
      {status.last_run ? (
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30"
           data-testid="contact-finder-last-run">
          Last run {status.last_run.status} · {status.last_run.updated?.toLocaleString() || 0} named ·{" "}
          {new Date(status.last_run.started_at).toLocaleString()}
        </p>
      ) : null}
    </Panel>
  );
};

const RosterPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    adminApi.get("/crm/rosters/status").then(({ data }) => setStatus(data)).catch(() => {});

  useEffect(() => { load(); }, []);

  if (!status) return null;

  const run = async () => {
    setBusy(true);
    try {
      await adminApi.post("/crm/ingest/rosters", { year: 2024, limit: 25 });
      toast.success("Roster import started");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="PEO client rosters" testid="panel-rosters">
      <div className="grid gap-5 sm:grid-cols-3">
        {[["PEO filings on file", status.peo_filers_on_file],
          ["PEOs with a roster", status.peos_with_rosters],
          ["Named employers", status.employers_on_rosters]].map(([label, value]) => (
          <div key={label}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">{label}</p>
            <p className="mt-1.5 font-serif text-2xl leading-none text-cream">{value}</p>
          </div>
        ))}
      </div>

      {status.configured ? (
        <div className="mt-5 space-y-3">
          <p className="text-sm leading-relaxed text-cream/55" data-testid="roster-ready">
            The image service is connected. A run reads each PEO filing's attachments, pulls out the
            named participating employers, and confirms the PEO on every account it matches.
          </p>
          <button type="button" onClick={run} disabled={busy} className={buttonClass}
                  data-testid="roster-run-button">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Import rosters
          </button>
        </div>
      ) : (
        <p className="mt-5 text-sm leading-relaxed text-cream/55" data-testid="roster-waiting">
          {status.waiting_on}
        </p>
      )}

      {status.last_run ? (
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30"
           data-testid="roster-last-run">
          Last run {status.last_run.status} ·{" "}
          {status.last_run.processed || 0}/{status.last_run.total || 0} filings ·{" "}
          {new Date(status.last_run.started_at).toLocaleString()}
        </p>
      ) : null}
    </Panel>
  );
};

const DolRequestPanel = () => {
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    adminApi.get("/crm/outreach/dol-request").then(({ data }) => setDraft(data)).catch(() => {});
  }, []);

  if (!draft) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${draft.subject}\n\n${draft.body}`);
      toast.success("Request copied");
    } catch {
      toast.error("Your browser blocked the clipboard — select the text and copy it");
    }
  };

  return (
    <Panel title="Unlock PEO client rosters" testid="panel-dol-request">
      <p className="text-sm leading-relaxed text-cream/55" data-testid="dol-why">{draft.why}</p>
      <ul className="mt-4 space-y-2">
        {draft.notes.map((note) => (
          <li key={note} className="flex gap-2 text-xs leading-relaxed text-cream/50">
            <span className="text-gold">·</span>{note}
          </li>
        ))}
      </ul>
      <div className="mt-5 rounded-lg border border-cream/10 bg-cream/5 p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
          To {draft.to}
        </p>
        <p className="mt-2 text-sm text-cream/90" data-testid="dol-subject">{draft.subject}</p>
        <p className="mt-3 whitespace-pre-line text-xs leading-relaxed text-cream/55"
           data-testid="dol-body">{draft.body}</p>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <a className={buttonClass} data-testid="dol-open-email"
           href={`mailto:${draft.to}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`}>
          <Mail className="h-3.5 w-3.5" /> Open in email
        </a>
        <button type="button" onClick={copy} data-testid="dol-copy"
                className="inline-flex items-center gap-2 rounded-full border border-cream/15 px-4 py-2 text-xs text-cream/70 transition-colors duration-200 hover:border-gold/50 hover:text-cream">
          <Copy className="h-3.5 w-3.5" /> Copy
        </button>
      </div>
    </Panel>
  );
};

export default function AdminData() {
  const { user } = useAdminAuth();
  const [runs, setRuns] = useState([]);
  const [form, setForm] = useState({
    year: 2024,
    states: "TX",
    min_participants: 25,
    max_participants: 2000,
    include_pension_only: true,
    refresh_download: false,
  });
  const [busy, setBusy] = useState(false);

  const load = () =>
    adminApi.get("/crm/ingest/runs").then(({ data }) => setRuns(data.runs)).catch(() => {});

  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, []);

  const startIngest = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await adminApi.post("/crm/ingest/form5500", {
        year: Number(form.year),
        states: form.states.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
        min_participants: Number(form.min_participants),
        max_participants: Number(form.max_participants),
        include_pension_only: form.include_pension_only,
        refresh_download: form.refresh_download,
      });
      toast.success("Ingest started — this runs in the background");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async (runId) => {
    try {
      await adminApi.delete(`/crm/ingest/runs/${runId}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const set = (key) => (event) =>
    setForm((prev) => ({
      ...prev,
      [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
    }));

  return (
    <div className="space-y-6" data-testid="admin-data-page">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Data sources</p>
        <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
          Form 5500 filings
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-cream/50">
          Public DOL/EBSA filings. Each load pulls the latest annual file, keeps the most recent filing
          per plan, and refreshes fit scores. Existing stages, owners and notes are never overwritten.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <Panel title="Run a load" testid="panel-ingest-form">
          {user?.role !== "admin" ? (
            <p className="text-sm text-cream/40">Only admins can run a data load.</p>
          ) : (
            <form onSubmit={startIngest} className="space-y-3" data-testid="ingest-form">
              <div className="space-y-1.5">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                  Filing year
                </label>
                <input type="number" min="2009" max="2030" value={form.year} onChange={set("year")}
                       className={inputClass} data-testid="ingest-year-input" />
              </div>
              <div className="space-y-1.5">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                  States (comma separated)
                </label>
                <input value={form.states} onChange={set("states")} className={inputClass}
                       data-testid="ingest-states-input" />
              </div>
              <div className="flex gap-2">
                <input type="number" min="0" value={form.min_participants} onChange={set("min_participants")}
                       placeholder="Min lives" className={inputClass} data-testid="ingest-min-input" />
                <input type="number" min="1" value={form.max_participants} onChange={set("max_participants")}
                       placeholder="Max lives" className={inputClass} data-testid="ingest-max-input" />
              </div>
              <label className="flex items-center gap-2 text-xs text-cream/60">
                <input type="checkbox" checked={form.include_pension_only}
                       onChange={set("include_pension_only")}
                       data-testid="ingest-pension-checkbox" className="accent-[#D4AF37]" />
                Include retirement-only filers (the &ldquo;likely on a PEO&rdquo; signal)
              </label>
              <label className="flex items-center gap-2 text-xs text-cream/60">
                <input type="checkbox" checked={form.refresh_download} onChange={set("refresh_download")}
                       data-testid="ingest-refresh-checkbox" className="accent-[#D4AF37]" />
                Re-download the DOL file first
              </label>
              <button type="submit" disabled={busy} className={buttonClass} data-testid="ingest-submit">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Start load
              </button>
            </form>
          )}
        </Panel>

        <div className="lg:col-span-2">
          <Panel title="Load history" testid="panel-ingest-runs">
            <ul className="divide-y divide-cream/10">
              {runs.map((run) => (
                <li key={run.id} className="py-3" data-testid={`run-${run.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-cream/85">
                      {run.params?.year} · {(run.params?.states || []).join(", ")} ·{" "}
                      {run.params?.min_participants}–{run.params?.max_participants} lives
                    </p>
                    <span
                      className={`font-mono text-[10px] uppercase tracking-[0.2em] ${
                        run.status === "complete"
                          ? "text-sage"
                          : run.status === "failed"
                          ? "text-terra"
                          : "text-gold"
                      }`}
                    >
                      {run.status}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-cream/40">
                    {run.created != null
                      ? `${run.created.toLocaleString()} new · ${run.updated?.toLocaleString() || 0} refreshed · `
                      : ""}
                    {run.processed?.toLocaleString() || 0}/{run.total?.toLocaleString() || 0} processed ·
                    started {new Date(run.started_at).toLocaleString()} by {run.started_by}
                  </p>
                  {run.error ? (
                    <p className="mt-1 text-xs text-terra" data-testid={`run-error-${run.id}`}>
                      {run.error}
                    </p>
                  ) : null}
                  {run.status !== "running" ? (
                    <button type="button" onClick={() => dismiss(run.id)}
                            data-testid={`run-dismiss-${run.id}`}
                            className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/25 transition-colors duration-200 hover:text-terra">
                      Dismiss
                    </button>
                  ) : null}
                </li>
              ))}
              {runs.length === 0 ? (
                <li className="py-3 text-sm text-cream/40" data-testid="runs-empty">
                  No loads yet.
                </li>
              ) : null}
            </ul>
          </Panel>
        </div>
      </div>

      <DolRequestPanel />
      <RosterPanel />
      <ContactFinderPanel />
      <TestDataPanel />
    </div>
  );
}
